import { DebugHistoryList } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugHistoryList';
import { DebugRecord } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugRecord';
import { useDebugHistory } from 'Modules/softphone/components/SoftphoneDevTools/hooks';
import {
  groupDebugInteractions,
  type DebugInteraction,
} from 'Modules/softphone/components/SoftphoneDevTools/utils';
import {
  DebugService,
  type CallDebugInfo,
} from 'Modules/softphone/services/DebugService';
import {
  buildDebugFilename,
  buildDebugReport,
  buildHistoryFilename,
  buildHistoryReport,
  buildInteractionFilename,
  buildInteractionReport,
  downloadTextFile,
} from 'Modules/softphone/services/DebugService/buildDebugReport';
import React, { useCallback, useMemo, useState } from 'react';

/**
 * DebugTab - What happened on calls that already ended
 *
 * List → record → section. The tab used to render every captured leg
 * expanded inline, which made a two-leg warm transfer a scroll rather than a
 * comparison.
 */
export const DebugTab: React.FC = () => {
  const { debugHistory, isLoading, clearHistory } = useDebugHistory();
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const debugService = DebugService.getInstance();

  const interactions = useMemo(
    () => groupDebugInteractions(debugHistory),
    [debugHistory]
  );

  // The history refreshes every 2s while the panel is open, so the selection is
  // held as an id rather than as a record: the record object is replaced on
  // every refresh, and the open leg has to survive that.
  const selected = useMemo(
    () => debugHistory.find((info) => info.callId === selectedCallId) ?? null,
    [debugHistory, selectedCallId]
  );

  const availableLegIds = useMemo(
    () => debugHistory.map((info) => info.callId),
    [debugHistory]
  );

  const handleDownload = useCallback(
    (info: CallDebugInfo) => {
      // Prefer the service, which has the live peer connection; fall back to
      // the persisted record. Both produce the same file.
      if (debugService.get(info.callId)) {
        debugService.download(info.callId);
        return;
      }

      // Same builder the service uses, so a call exports identically whether
      // it is still cached or was rehydrated from storage after a reload.
      downloadTextFile(buildDebugFilename(info), buildDebugReport(info));
    },
    [debugService]
  );

  /**
   * Download every leg of one interaction in a single file. A warm transfer or
   * a conference is not diagnosable one leg at a time.
   */
  const handleDownloadInteraction = useCallback(
    (interaction: DebugInteraction) => {
      if (interaction.legs.length <= 1) {
        handleDownload(interaction.legs[0]);
        return;
      }

      downloadTextFile(
        buildInteractionFilename(interaction.legs),
        buildInteractionReport(interaction.legs)
      );
    },
    [handleDownload]
  );

  /**
   * The whole history as one file, interaction by interaction, so a session
   * with several related calls travels as a single attachment.
   */
  const handleDownloadAll = useCallback(() => {
    if (debugHistory.length === 0) {
      return;
    }

    downloadTextFile(
      buildHistoryFilename(),
      buildHistoryReport(interactions.map((interaction) => interaction.legs))
    );
  }, [debugHistory.length, interactions]);

  const handleClear = useCallback(() => {
    setSelectedCallId(null);
    void clearHistory();
  }, [clearHistory]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-[11.5px] text-zinc-400">Loading debug history…</span>
      </div>
    );
  }

  if (selected) {
    return (
      <DebugRecord
        info={selected}
        availableLegIds={availableLegIds}
        onBack={() => setSelectedCallId(null)}
        onOpenLeg={setSelectedCallId}
        onDownload={() => handleDownload(selected)}
      />
    );
  }

  return (
    <DebugHistoryList
      interactions={interactions}
      recordCount={debugHistory.length}
      onOpenLeg={(info) => setSelectedCallId(info.callId)}
      onDownloadInteraction={handleDownloadInteraction}
      onDownloadAll={handleDownloadAll}
      onClear={handleClear}
    />
  );
};
